import { usePostHog as usePostHogContext } from 'posthog-js/react'
import { useCallback } from 'react'
import { IPosthogEvent, IUsePostHogReturn } from './types'
import { useRouter } from 'next/router'
import { useQuery } from '@apollo/client'
import { graphql } from '@generated/gql'

const CompanyNameGetDocument = graphql(/* GraphQL */ `
  query CompanyNameGet($input: CompanyGetInput!) {
    company {
      get(input: $input) {
        company {
          id
          name
        }
      }
    }
  }
`)

export const usePostHog = <T>(): IUsePostHogReturn<T> => {
  const client = usePostHogContext()
  const router = useRouter()

  const { data: companyNameGetData } = useQuery(CompanyNameGetDocument, {
    variables: {
      input: {
        companyId: String(router.query.companyId),
      },
    },
    skip: !router.query.companyId,
  })

  const setPosthogGroup = useCallback(
    (data = {}) => {
      if (!router.query.companyId) {
        client.group('company', String(router.query.companyId), {
          name: companyNameGetData?.company?.get?.company?.name,
          ...data,
        })
      }
    },
    [
      client,
      router.query.companyId,
      companyNameGetData?.company?.get?.company?.name,
    ],
  )

  const capturePosthogEvent = (eventName: string, data: IPosthogEvent<T>) => {
    if (client.__loaded) {
      setPosthogGroup()
      client.capture(eventName, data)
    }
  }

  const identifyPosthog = useCallback(
    (event) => {
      if (client.__loaded) {
        setPosthogGroup()
        client.identify(event)
      }
    },
    [client, setPosthogGroup],
  )

  const setPosthogPeople = useCallback(
    (
      event: IPosthogEvent<T>,
      ingestMethod: 'set' | 'set_once' = 'set',
      groupData?: Record<string, unknown>,
    ) => {
      if (client.__loaded) {
        setPosthogGroup(groupData)
        client.people[ingestMethod](event)
      }
    },
    [client.__loaded, client.people, setPosthogGroup],
  )

  const resetPosthog = useCallback(() => {
    if (client.__loaded) client.reset()
  }, [client])

  return [
    client,
    {
      capturePosthogEvent,
      setPosthogPeople,
      identifyPosthog,
      resetPosthog,
      setPosthogGroup,
    },
  ]
}
